import AutoCatalogSelectorStore from "./AutoCatalogSelectorStore";
import {observable} from "mobx";
import api from "../../Services/Api";
import {AutoCatalogByEntitiesDto} from "./Dtos";

export default class AutoCatalogStore {
    @observable selectors: AutoCatalogSelectorStore[] = []
    @observable currentSelector?: AutoCatalogSelectorStore;
    @observable entityHrefIds: string[] = [];
    @observable saving: boolean = false;
    @observable saved: boolean = true;
    @observable savedCationVisible: boolean = false;
    @observable loading: boolean = true;

    async init(entityHrefIds: string[]) {
        this.cleanup();
        this.entityHrefIds = entityHrefIds;
        if (this.entityHrefIds.length !== 0) {
            await this.load();
        }
    }

    async load() {
        try {
            this.setLoading(true);
            const result = await api.post('autocatalog/byEntities', {
                entityHrefIds: this.entityHrefIds,
            });
            if (result.success) {
                const entities = result.data as AutoCatalogByEntitiesDto[];
                for (const entity of entities) {
                    const newSelector = new AutoCatalogSelectorStore(() => this.setSaved(true), () => this.setSaved(false));
                    this.selectors.push(newSelector);
                    await newSelector.init(entity)
                }
                if (this.selectors.length !== 0) {
                    this.select(this.selectors[0]);
                }
                this.setSaved(true);
            }
        }
        finally {
            this.setLoading(false);
        }
    }

    select(selector: AutoCatalogSelectorStore) {
        this.currentSelector = selector;
    }

    tryAdd() {
        const unAppliedSelector = this.selectors
            .find(selector => !selector.applied);
        if (unAppliedSelector) {
            this.currentSelector = unAppliedSelector;
        } else {
            this.add();
        }
    }

    private add() {
        const newSelector = new AutoCatalogSelectorStore(() => this.setSaved(true), () => this.setSaved(false));
        this.selectors.push(newSelector);
        this.currentSelector = newSelector;
    }

    delete(deletingSelector: AutoCatalogSelectorStore) {
        const deletingCurrent = this.currentSelector === deletingSelector;
        this.selectors = this.selectors.filter(s => s !== deletingSelector);
        if (this.selectors.length === 0) {
            this.add();
        } else if (deletingCurrent) {
            this.currentSelector = this.selectors[0];
        }
        this.setSaved(false);
    }

    get hasApplied(): boolean {
        return this.selectors.some(selector => selector.applied);
    }

    get canAdd(): boolean {
        return (!this.currentSelector || this.currentSelector.applied) && this.selectors.filter(x => x.applied).length <= 20;
    }

    cleanup() {
        this.selectors = [];
        this.currentSelector = undefined;
        this.saving = false;
        this.saved = true;
        this.loading = false;
        this.savedCationVisible = false;
    }

    async save() {
        if (!this.canSave) {
            return;
        }
        try {
            this.setSaving(true);
            const result = await api.post('autocatalog/save', {
                entityHrefIds: this.entityHrefIds,
                data: this.selectors.filter(x => x.applied).map(x => ({
                    id: x.id,
                    generationId: x.appliedCar!.generation?.id,
                    modificationId: x.appliedCar!.modification?.id,
                    complectationId: x.appliedCar!.complectation?.id,
                    body: x.appliedCar!.body ? {
                        doorsId: x.appliedCar!.body!.doorsId,
                        typeId: x.appliedCar!.body!.typeId
                    } : undefined
                }))
            })
            if (result.success) {
                this.setSaved(true);
                this.showSavedCation();
            }
        } finally {
            this.setSaving(false);
        }
    }

    get canSave(): boolean {
        return !this.saved
            && !this.saving
            && this.selectors.every(x => !x.applied || !x.hasChanges)
    }

    private setSaving(value: boolean) {
        this.saving = value;
    }

    private setSaved(value: boolean) {
        this.saved = value;
        if (!value) {
            this.savedCationVisible = false;
        }
    }
    
    private setLoading(value: boolean) {
        this.loading = value;
    }
    
    private showSavedCation() {
        this.savedCationVisible = true;
    }
}